import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import WebSocketComponent from './Requests';
import { useAuth } from '../Context/AuthProvider';
import {
  MA_MD_DEBUG_PREFIX,
  normalizeCusipList,
  describeCusipCollection,
} from './maInventoryMarketDataUtils';

// Create context with initial values
const WebSocketContext = createContext({
  connectionState: 'disconnected',
  setMAInventoryVisibleCusips: () => {},
  clearMAInventoryVisibleCusips: () => {},
  maInventoryVisibleCusips: [],
  maInventoryRequestTraceId: '',
  setMAInventoryRequestTraceId: () => {},
  subscribePageGroup: () => {},
  unsubscribePageGroup: () => {},
  activePageGroups: new Set(),
});

export const WebSocketProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [connectionState, setConnectionState] = useState('disconnected');
  const [shouldRenderWS, setShouldRenderWS] = useState(false);
  const [maInventoryVisibleCusips, setMaInventoryVisibleCusips] = useState([]);
  const [maInventoryRequestTraceId, setMaInventoryRequestTraceId] = useState('');

  // Tracks which page groups are currently active based on mounted components
  const [activePageGroups, setActivePageGroups] = useState(new Set());
  const pageGroupRefs = useRef({});

  // ---------------------------------------------------------------------------
  // subscribePageGroup & unsubscribePageGroup — handle reference-counted subs
  // groupName: 'orders' | 'counterParties' | 'sessionUserConfig' | 'accountPositions'
  // ---------------------------------------------------------------------------
  const subscribePageGroup = useCallback((groupName) => {
    if (!groupName) return;
    
    // Initialize or increment ref count
    const currentCount = pageGroupRefs.current[groupName] || 0;
    pageGroupRefs.current[groupName] = currentCount + 1;
    
    if (pageGroupRefs.current[groupName] === 1) {
      console.log(`[WS-Provider] PAGE_GROUP_SUBSCRIBE group=${groupName} — scheduling subscription`);
      setActivePageGroups((prev) => {
        const next = new Set(prev);
        next.add(groupName);
        return next;
      });
    } else {
      console.log(`[WS-Provider] PAGE_GROUP_SUBSCRIBE group=${groupName} — ref count to ${pageGroupRefs.current[groupName]}`);
    }
  }, []);

  const unsubscribePageGroup = useCallback((groupName) => {
    if (!groupName) return;
    
    if (pageGroupRefs.current[groupName] > 0) {
      pageGroupRefs.current[groupName] -= 1;
      console.log(`[WS-Provider] PAGE_GROUP_UNSUBSCRIBE group=${groupName} — decremented to ${pageGroupRefs.current[groupName]}`);
    }

    if (pageGroupRefs.current[groupName] === 0) {
      console.log(`[WS-Provider] PAGE_GROUP_UNSUBSCRIBE group=${groupName} — removing from active groups`);
      setActivePageGroups((prev) => {
        const next = new Set(prev);
        next.delete(groupName);
        return next;
      });
    }
  }, []);

  const setMAInventoryVisibleCusips = useCallback((cusips, reason = 'page-update') => {
    const normalizedCusips = normalizeCusipList(cusips);

    console.log(
      `${MA_MD_DEBUG_PREFIX} PROVIDER_SET reason=${reason} count=${normalizedCusips.length} cusips=${describeCusipCollection(normalizedCusips)}`
    );

    setMaInventoryVisibleCusips((currentCusips) => {
      const currentKey = currentCusips.join('|');
      const nextKey = normalizedCusips.join('|');
      if (currentKey === nextKey) {
        console.log(`${MA_MD_DEBUG_PREFIX} PROVIDER_SET_SKIPPED reason=${reason} no-change`);
        return currentCusips;
      }

      return normalizedCusips;
    });
  }, []);

  const clearMAInventoryVisibleCusips = useCallback((reason = 'page-cleanup') => {
    console.log(`${MA_MD_DEBUG_PREFIX} PROVIDER_CLEAR reason=${reason}`);
    setMaInventoryVisibleCusips((currentCusips) => {
      if (!currentCusips.length) {
        console.log(`${MA_MD_DEBUG_PREFIX} PROVIDER_CLEAR_SKIPPED reason=${reason} already-empty`);
        return currentCusips;
      }
      return [];
    });
  }, []);

  const setMAInventoryRequestTraceIdContext = useCallback((traceId, reason = 'api-request') => {
    const normalizedTraceId = traceId == null ? '' : String(traceId).trim();

    console.log(`${MA_MD_DEBUG_PREFIX} PROVIDER_TRACE_ID_SET reason=${reason} traceId=${normalizedTraceId || '<empty>'}`);
    setMaInventoryRequestTraceId(normalizedTraceId);
  }, []);

  // Listen for connection state changes
  useEffect(() => {
    const handleConnectionChange = (event) => {
      setConnectionState(event.detail);
    };

    window.addEventListener("websocketConnectionStateChange", handleConnectionChange);
    return () => {
      window.removeEventListener("websocketConnectionStateChange", handleConnectionChange);
    };
  }, []);

  // Control WebSocket component mounting/unmounting
  useEffect(() => {
    if (isLoggedIn) {
      setShouldRenderWS(true);
    } else {
      // Force unmount when logged out + reset groups
      setShouldRenderWS(false);
      setActivePageGroups(new Set());
      pageGroupRefs.current = {};
      clearMAInventoryVisibleCusips('logout');
    }
  }, [clearMAInventoryVisibleCusips, isLoggedIn]);

  // Only render WebSocketComponent if user is logged in
  return (
    <WebSocketContext.Provider
      value={{
        connectionState,
        setMAInventoryVisibleCusips,
        clearMAInventoryVisibleCusips,
        maInventoryVisibleCusips,
        maInventoryRequestTraceId,
        setMAInventoryRequestTraceId: setMAInventoryRequestTraceIdContext,
        subscribePageGroup,
        unsubscribePageGroup,
        activePageGroups,
      }}
    >
      {shouldRenderWS && (
        <WebSocketComponent
          onConnectionStateChange={setConnectionState}
          desiredMAInventoryCusips={maInventoryVisibleCusips}
          maInventoryRequestTraceId={maInventoryRequestTraceId}
          pageSubscriptionGroups={activePageGroups}
        />
      )}
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const usePageSubscription = (groupName) => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('usePageSubscription must be used within a WebSocketProvider');
  }
  const { subscribePageGroup, unsubscribePageGroup } = context;

  useEffect(() => {
    if (groupName) {
      subscribePageGroup(groupName);
      return () => {
        unsubscribePageGroup(groupName);
      };
    }
  }, [groupName, subscribePageGroup, unsubscribePageGroup]);
};
